import type { FC } from 'react';
import React, { useEffect } from 'react';
import {
  Desktop,
  Mobile,
  useTranslation,
  useTracking,
} from '@edreamsodigeo/frontend-core';
import {
  Box,
  Button,
  DrawerBottomSheet,
  DrawerBottomSheetContent,
  SystemModalHeader,
  Heading,
  SystemModal,
  ModalContent,
  SystemModalCloseIcon,
  SystemModalIcon,
  useDrawer,
  Glyph,
} from '@edreamsodigeo/prisma-design-system';
import { Text } from '@edreamsodigeo/prisma-design-system/deprecated';

import {
  useFreeCancellationDrawerActions,
  useIsFreeCancellationDrawerOpen,
} from '@/clientState/providers/FreeCancellationDrawerProvider';

export interface FreeCancellationDrawerMainProps {
  labelPage: string;
}

export interface FreeCancellationDrawerProps {
  open: boolean;
  onClose: () => void;
  labelPage: string;
}

export interface FreeCancellationDrawerHeaderProps {
  onClose: () => void;
}

export interface FreeCancellationDrawerContentProps {
  onClose: () => void;
}

export const FREE_CANCELLATION_POPUP_ACTION = '24h-cancellation-msg_popup';
export const FREE_CANCELLATION_POPUP_ONLOAD_LABEL =
  '24h-cancellation-msg_pop-up_onload_pag:';
export const FREE_CANCELLATION_POPUP_CLOSE_CROSS =
  '24h-cancellation-msg_pop-up_close-x_pag:';
export const FREE_CANCELLATION_POPUP_CLOSE_CONFIRM_BUTTON =
  '24h-cancellation-msg_pop-up_close-button_pag:';

export const FreeCancellationDrawerHeader: FC<
  FreeCancellationDrawerHeaderProps
> = ({ onClose }) => (
  <SystemModalHeader type="success">
    <SystemModalCloseIcon onClose={onClose} />
    <SystemModalIcon>
      <Glyph
        color="green.50"
        size="medium"
        category="TimeAndDates"
        name="Future"
      />
    </SystemModalIcon>
  </SystemModalHeader>
);

export const FreeCancellationDrawerContent: FC<
  FreeCancellationDrawerContentProps
> = ({ onClose }) => {
  const { t } = useTranslation();
  return (
    <>
      <Heading type="2" weight="medium" textAlign="center">
        {t('freeCancellation.pax.popup.title')}
      </Heading>
      <Text textAlign="center">{t('freeCancellation.pax.popup.message')}</Text>
      <Box
        paddingLeft="base"
        paddingRight="base"
        paddingBottom="1x_small"
        paddingTop="base"
      >
        <Button onClick={onClose} fullWidth type="primary">
          {t('freeCancellation.pax.popup.button')}
        </Button>
      </Box>
    </>
  );
};

export const FreeCancellationDrawerBase: FC<FreeCancellationDrawerProps> = ({
  open,
  onClose,
  labelPage,
}) => {
  const { trackEvent } = useTracking();

  const { state, transitionDuration } = useDrawer({
    isOpen: open,
    transitionDuration: 300,
  });

  const handleClose = (sourceLabel: string): void => {
    trackEvent({
      action: FREE_CANCELLATION_POPUP_ACTION,
      label: sourceLabel + labelPage,
    });
    onClose();
  };

  useEffect(() => {
    if (open) {
      trackEvent({
        action: FREE_CANCELLATION_POPUP_ACTION,
        label: FREE_CANCELLATION_POPUP_ONLOAD_LABEL + labelPage,
      });
    }
  }, [open, trackEvent, labelPage]);

  return (
    <>
      <Desktop>
        <SystemModal
          open={open}
          onClose={(): void =>
            handleClose(FREE_CANCELLATION_POPUP_CLOSE_CONFIRM_BUTTON)
          }
        >
          <FreeCancellationDrawerHeader
            onClose={(): void =>
              handleClose(FREE_CANCELLATION_POPUP_CLOSE_CROSS)
            }
          />
          <ModalContent
            sx={{
              padding: '2x_big',
              display: 'flex',
              flexDirection: 'column',
              gap: '1x_big',
            }}
          >
            <FreeCancellationDrawerContent
              onClose={(): void =>
                handleClose(FREE_CANCELLATION_POPUP_CLOSE_CONFIRM_BUTTON)
              }
            />
          </ModalContent>
        </SystemModal>
      </Desktop>
      <Mobile>
        <DrawerBottomSheet
          state={state}
          transitionDuration={transitionDuration}
          onBackdropClick={(): void =>
            handleClose(FREE_CANCELLATION_POPUP_CLOSE_CONFIRM_BUTTON)
          }
        >
          <FreeCancellationDrawerHeader
            onClose={(): void =>
              handleClose(FREE_CANCELLATION_POPUP_CLOSE_CROSS)
            }
          />
          <DrawerBottomSheetContent
            sx={{
              padding: '2x_big',
              display: 'flex',
              flexDirection: 'column',
              gap: '1x_big',
            }}
          >
            <FreeCancellationDrawerContent
              onClose={(): void =>
                handleClose(FREE_CANCELLATION_POPUP_CLOSE_CONFIRM_BUTTON)
              }
            />
          </DrawerBottomSheetContent>
        </DrawerBottomSheet>
      </Mobile>
    </>
  );
};

export const FreeCancellationDrawer: FC<FreeCancellationDrawerMainProps> = ({
  labelPage,
}) => {
  const open = useIsFreeCancellationDrawerOpen();
  const { close } = useFreeCancellationDrawerActions();

  return (
    <FreeCancellationDrawerBase
      open={open}
      onClose={close}
      labelPage={labelPage}
    />
  );
};

export default FreeCancellationDrawer;
